import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  CareerChallengeJudgeDto,
  CareerChallengeRubricScoreDto,
  CareerChallengeSubmissionDto
} from '../../../../models';
import { CareerChallengeAdminStore } from '../shared/career-challenge-admin.store';

@Component({
  selector: 'app-career-challenge-submissions-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatCardModule, MatButtonModule, MatProgressBarModule, MatSnackBarModule],
  templateUrl: './submissions-tab.component.html',
  styleUrl: './tab-shared.scss'
})
export class CareerChallengeSubmissionsTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly store = inject(CareerChallengeAdminStore);
  private readonly snackBar = inject(MatSnackBar);

  loading = false;
  busy = false;
  submissions: CareerChallengeSubmissionDto[] = [];
  drafts: Record<
    string,
    Record<
      string,
      {
        agreed: boolean;
        comments: string;
        scores: CareerChallengeRubricScoreDto[];
        comprehensionAgreed?: boolean;
        comprehensionScoreAdjustment?: number | null;
        comprehensionComments?: string;
      }
    >
  > = {};

  get id(): string {
    return this.route.parent?.snapshot.paramMap.get('id') ?? '';
  }

  get judges(): CareerChallengeJudgeDto[] {
    return this.store.challenge()?.judges ?? [];
  }

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      await this.store.load(this.id);
      this.submissions = await firstValueFrom(this.api.listAdminCareerChallengeSubmissions(this.id));
      if (!this.submissions?.length && this.store.challenge()?.submissions?.length) {
        this.submissions = this.store.challenge()!.submissions;
      }
      this.seedDrafts();
    } catch {
      this.submissions = this.store.challenge()?.submissions ?? [];
      this.seedDrafts();
      this.snackBar.open('Could not refresh submissions', 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  judgeProgress(submission: CareerChallengeSubmissionDto): string {
    const total = this.judges.length;
    if (!total) {
      return 'No judges — AI score is final';
    }
    const done = (submission.judgeEvaluations ?? []).filter(
      (e) => e.status === 'submitted' || e.status === 'auto_accepted_ai'
    ).length;
    return `${done}/${total} judged`;
  }

  canRestartComprehension(submission: CareerChallengeSubmissionDto): boolean {
    const members = submission.memberComprehensions ?? [];
    return !submission.teamId
      && members.length === 1
      && members[0].status === 'completed'
      && ['comprehension_pending', 'scored', 'evaluated', 'failed_eval'].includes(submission.status ?? '');
  }

  async restartComprehension(submission: CareerChallengeSubmissionDto): Promise<void> {
    if (this.busy || !this.canRestartComprehension(submission)) return;
    if (!window.confirm(
      'Restart this learner\'s comprehension? Repository review will run again and a new conversation will be created. '
      + 'The current submission score will be cleared. Existing conversation messages will remain in history. '
      + 'Published skill results or later learner progression will prevent this action. Continue?'
    )) return;
    this.busy = true;
    try {
      await firstValueFrom(this.api.restartCareerChallengeComprehension(this.id, submission.id));
      this.snackBar.open('Repository review queued. A new comprehension conversation will follow.', 'Close', { duration: 5000 });
      await this.load();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 7000 });
    } finally {
      this.busy = false;
    }
  }

  draft(submissionId: string, judgeId: string) {
    return this.drafts[submissionId]?.[judgeId];
  }

  onAgree(submission: CareerChallengeSubmissionDto, judgeId: string, agreed: boolean): void {
    const d = this.draft(submission.id, judgeId);
    if (!d) {
      return;
    }
    d.agreed = agreed;
    if (agreed) {
      d.scores = (submission.rubricScores ?? []).map((s) => ({ ...s, rationale: s.rationale ?? '' }));
    }
  }

  onComprehensionAgree(
    submission: CareerChallengeSubmissionDto,
    judgeId: string,
    agreed: boolean
  ): void {
    const d = this.draft(submission.id, judgeId);
    if (!d) {
      return;
    }
    d.comprehensionAgreed = agreed;
  }

  async submit(submission: CareerChallengeSubmissionDto, judgeId: string): Promise<void> {
    const d = this.draft(submission.id, judgeId);
    if (!d) {
      return;
    }
    this.busy = true;
    try {
      await firstValueFrom(
        this.api.submitCareerChallengeJudgeEvaluation(this.id, submission.id, judgeId, {
          agreedWithAi: d.agreed,
          comments: d.comments.trim() || null,
          rubricScores: d.scores.map((s) => ({
            criterion: s.criterion ?? s.key,
            key: s.key,
            score: Number(s.score),
            weight: s.weight,
            rationale: (s.rationale ?? '').trim()
          })),
          comprehensionAgreedWithAi: d.comprehensionAgreed ?? null,
          comprehensionScoreAdjustment:
            d.comprehensionScoreAdjustment != null ? Number(d.comprehensionScoreAdjustment) : null,
          comprehensionComments: d.comprehensionComments?.trim() || null
        })
      );
      this.snackBar.open('Judge evaluation saved', 'Close', { duration: 2200 });
      await this.load();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 4000 });
    } finally {
      this.busy = false;
    }
  }

  private seedDrafts(): void {
    for (const submission of this.submissions) {
      this.drafts[submission.id] ??= {};
      for (const judge of this.judges) {
        const existing = (submission.judgeEvaluations ?? []).find((e) => e.judgeId === judge.id);
        this.drafts[submission.id][judge.id] = {
          agreed: existing?.agreedWithAi ?? true,
          comments: existing?.comments ?? '',
          scores: (existing?.rubricScores?.length ? existing.rubricScores : submission.rubricScores ?? []).map((s) => ({
            ...s,
            rationale: s.rationale ?? ''
          })),
          comprehensionAgreed: existing?.comprehensionAgreedWithAi ?? true,
          comprehensionScoreAdjustment: existing?.comprehensionScoreAdjustment ?? null,
          comprehensionComments: existing?.comprehensionComments ?? ''
        };
      }
    }
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = error.error?.detail ?? error.error?.title ?? error.error;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return 'Request failed';
  }
}
